// hooks/usePagination.js
import { useState, useMemo, useCallback } from 'react';
import { APP_CONFIG } from '../config/app.config';

/**
 * usePagination Hook
 * Handles pagination logic and state management
 */
export const usePagination = (data = [], options = {}) => {
  const {
    initialPage = 1,
    initialPageSize = APP_CONFIG.PAGINATION.DEFAULT_PAGE_SIZE,
    pageSizeOptions = APP_CONFIG.PAGINATION.PAGE_SIZE_OPTIONS,
    maxPageSize = APP_CONFIG.PAGINATION.MAX_PAGE_SIZE
  } = options;

  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);

  // Calculate pagination values
  const paginationInfo = useMemo(() => {
    const totalItems = data.length;
    const totalPages = Math.ceil(totalItems / pageSize) || 1;
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, totalItems);
    const currentItems = data.slice(startIndex, endIndex);
    
    const hasNextPage = currentPage < totalPages;
    const hasPreviousPage = currentPage > 1;
    
    const startItem = totalItems === 0 ? 0 : startIndex + 1;
    const endItem = endIndex;

    return {
      currentPage,
      pageSize,
      totalItems,
      totalPages,
      startIndex,
      endIndex,
      currentItems,
      hasNextPage,
      hasPreviousPage,
      startItem,
      endItem,
      isEmpty: totalItems === 0
    };
  }, [data, currentPage, pageSize]);

  // Navigation functions
  const goToPage = useCallback((page) => {
    const validPage = Math.max(1, Math.min(page, paginationInfo.totalPages));
    setCurrentPage(validPage);
  }, [paginationInfo.totalPages]);

  const goToNextPage = useCallback(() => {
    if (paginationInfo.hasNextPage) {
      setCurrentPage(prev => prev + 1);
    }
  }, [paginationInfo.hasNextPage]);

  const goToPreviousPage = useCallback(() => {
    if (paginationInfo.hasPreviousPage) {
      setCurrentPage(prev => prev - 1);
    }
  }, [paginationInfo.hasPreviousPage]);

  const goToFirstPage = useCallback(() => {
    setCurrentPage(1);
  }, []);

  const goToLastPage = useCallback(() => {
    setCurrentPage(paginationInfo.totalPages);
  }, [paginationInfo.totalPages]);

  // Page size functions
  const changePageSize = useCallback((newPageSize) => {
    const validPageSize = Math.min(newPageSize, maxPageSize);
    setPageSize(validPageSize);
    
    // Adjust current page to maintain roughly the same position
    const currentFirstItem = (currentPage - 1) * pageSize + 1;
    const newPage = Math.ceil(currentFirstItem / validPageSize);
    setCurrentPage(newPage);
  }, [currentPage, pageSize, maxPageSize]);

  // Reset pagination
  const reset = useCallback(() => {
    setCurrentPage(initialPage);
    setPageSize(initialPageSize);
  }, [initialPage, initialPageSize]);

  // Generate page numbers for pagination controls
  const getPageNumbers = useCallback((visiblePages = 5) => {
    const { totalPages, currentPage } = paginationInfo;
    const pages = [];
    
    if (totalPages <= visiblePages) {
      // Show all pages if total is less than visible pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Calculate start and end pages
      const halfVisible = Math.floor(visiblePages / 2);
      let startPage = Math.max(1, currentPage - halfVisible);
      let endPage = Math.min(totalPages, startPage + visiblePages - 1);
      
      // Adjust if we're near the end
      if (endPage - startPage < visiblePages - 1) {
        startPage = Math.max(1, endPage - visiblePages + 1);
      }
      
      // Add ellipsis and first page if needed
      if (startPage > 1) {
        pages.push(1);
        if (startPage > 2) {
          pages.push('...');
        }
      }
      
      // Add visible pages
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      // Add ellipsis and last page if needed
      if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
          pages.push('...');
        }
        pages.push(totalPages);
      }
    }
    
    return pages;
  }, [paginationInfo]);

  return {
    // Pagination info
    ...paginationInfo,
    
    // Navigation functions
    goToPage,
    goToNextPage,
    goToPreviousPage,
    goToFirstPage,
    goToLastPage,
    
    // Page size functions
    changePageSize,
    
    // Utility functions
    reset,
    getPageNumbers,
    
    // Configuration
    pageSizeOptions: pageSizeOptions.filter(size => size <= maxPageSize)
  };
};

/**
 * useServerPagination Hook
 * Handles server-side pagination
 */
export const useServerPagination = (options = {}) => {
  const {
    initialPage = 1,
    initialPageSize = APP_CONFIG.PAGINATION.DEFAULT_PAGE_SIZE,
    pageSizeOptions = APP_CONFIG.PAGINATION.PAGE_SIZE_OPTIONS,
    maxPageSize = APP_CONFIG.PAGINATION.MAX_PAGE_SIZE
  } = options;

  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [totalItems, setTotalItems] = useState(0);

  // Calculate derived values
  const totalPages = Math.ceil(totalItems / pageSize) || 1;
  const hasNextPage = currentPage < totalPages;
  const hasPreviousPage = currentPage > 1;
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  // Navigation functions
  const goToPage = useCallback((page) => {
    const validPage = Math.max(1, Math.min(page, totalPages));
    setCurrentPage(validPage);
    return validPage;
  }, [totalPages]);

  const goToNextPage = useCallback(() => {
    if (hasNextPage) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
      return newPage;
    }
    return currentPage;
  }, [currentPage, hasNextPage]);

  const goToPreviousPage = useCallback(() => {
    if (hasPreviousPage) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      return newPage;
    }
    return currentPage;
  }, [currentPage, hasPreviousPage]);

  const goToFirstPage = useCallback(() => {
    setCurrentPage(1);
    return 1;
  }, []);

  const goToLastPage = useCallback(() => {
    setCurrentPage(totalPages);
    return totalPages;
  }, [totalPages]);

  // Page size functions
  const changePageSize = useCallback((newPageSize) => {
    const validPageSize = Math.min(newPageSize, maxPageSize);
    setPageSize(validPageSize);
    
    // Maintain the same starting item when changing page size
    const currentFirstItem = (currentPage - 1) * pageSize + 1;
    const newPage = Math.ceil(currentFirstItem / validPageSize);
    setCurrentPage(newPage);
    
    return { page: newPage, pageSize: validPageSize };
  }, [currentPage, pageSize, maxPageSize]);

  // Update total items (usually called after fetching data)
  const updateTotalItems = useCallback((total) => {
    setTotalItems(total);
    
    // Adjust current page if it's beyond the new total pages
    const newTotalPages = Math.ceil(total / pageSize) || 1;
    if (currentPage > newTotalPages) {
      setCurrentPage(newTotalPages);
    }
  }, [currentPage, pageSize]);

  // Reset pagination
  const reset = useCallback(() => {
    setCurrentPage(initialPage);
    setPageSize(initialPageSize);
    setTotalItems(0);
  }, [initialPage, initialPageSize]);

  // Get query parameters for API calls
  const getQueryParams = useCallback(() => {
    return {
      page: currentPage,
      pageSize: pageSize,
      skip: (currentPage - 1) * pageSize,
      take: pageSize
    };
  }, [currentPage, pageSize]);

  // Generate page numbers for pagination controls
  const getPageNumbers = useCallback((visiblePages = 5) => {
    const pages = [];
    
    if (totalPages <= visiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      const halfVisible = Math.floor(visiblePages / 2);
      let startPage = Math.max(1, currentPage - halfVisible);
      let endPage = Math.min(totalPages, startPage + visiblePages - 1);
      
      if (endPage - startPage < visiblePages - 1) {
        startPage = Math.max(1, endPage - visiblePages + 1);
      }
      
      if (startPage > 1) {
        pages.push(1);
        if (startPage > 2) {
          pages.push('...');
        }
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
          pages.push('...');
        }
        pages.push(totalPages);
      }
    }
    
    return pages;
  }, [currentPage, totalPages]);

  return {
    // State
    currentPage,
    pageSize,
    totalItems,
    totalPages,
    hasNextPage,
    hasPreviousPage,
    startItem,
    endItem,
    isEmpty: totalItems === 0,
    
    // Navigation functions
    goToPage,
    goToNextPage,
    goToPreviousPage,
    goToFirstPage,
    goToLastPage,
    
    // Page size functions
    changePageSize,
    
    // Data functions
    updateTotalItems,
    
    // Utility functions
    reset,
    getQueryParams,
    getPageNumbers,
    
    // Configuration
    pageSizeOptions: pageSizeOptions.filter(size => size <= maxPageSize)
  };
};